/**
 * Vertical & lens correction: straighten converging walls and balance
 * perspective so every room looks architecturally crisp.
 *
 * This mirrors the worker's local perspective step but runs client-side for
 * interactive control. It is dependency-free — a homography (projective warp)
 * is solved from four corner correspondences and the image is resampled with
 * bilinear filtering, so there is no OpenCV.js download.
 *
 * Controls:
 *  - `vertical`   keystone: spreads the top (or bottom) of the frame to make
 *                 converging verticals parallel.
 *  - `horizontal` keystone: spreads the left (or right) edge for off-axis shots.
 *  - `rotate`     levels a tilted horizon (degrees).
 *  - `autoFit`    scales the sampled region inward so the corrected output has
 *                 no transparent borders (a small zoom/crop, like Lightroom's
 *                 "Constrain crop"). Default on.
 */

export interface VerticalsOptions {
  /** Vertical keystone, -1..1. +ve widens the top, -ve widens the bottom. */
  vertical: number;
  /** Horizontal keystone, -1..1. +ve widens the left, -ve widens the right. */
  horizontal: number;
  /** Roll correction in degrees, e.g. -10..10. +ve rotates clockwise. */
  rotate: number;
  /** Scale the sampled region in so there are no transparent borders. */
  autoFit: boolean;
}

export const DEFAULT_VERTICALS: VerticalsOptions = {
  vertical: 0,
  horizontal: 0,
  rotate: 0,
  autoFit: true,
};

/** Largest edge inset (as a fraction of width/height) at full slider travel. */
const MAX_INSET = 0.25;

const clamp = (v: number, min: number, max: number) =>
  v < min ? min : v > max ? max : v;

export const isIdentityVerticals = (o: VerticalsOptions): boolean =>
  Math.abs(o.vertical) < 1e-4 &&
  Math.abs(o.horizontal) < 1e-4 &&
  Math.abs(o.rotate) < 1e-3;

interface Pt {
  x: number;
  y: number;
}

/**
 * Solve the 8x8 linear system for the homography that maps the destination
 * (output) corners to the source corners. Corners are ordered TL, TR, BR, BL.
 * Returns [a,b,c,d,e,f,g,h] where, for an output point (x,y):
 *   denom = g*x + h*y + 1
 *   u = (a*x + b*y + c) / denom
 *   v = (d*x + e*y + f) / denom
 */
function solveHomography(dst: Pt[], src: Pt[]): number[] {
  const A: number[][] = [];
  const B: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = dst[i];
    const { x: u, y: v } = src[i];
    A.push([x, y, 1, 0, 0, 0, -x * u, -y * u]);
    B.push(u);
    A.push([0, 0, 0, x, y, 1, -x * v, -y * v]);
    B.push(v);
  }
  return gaussianSolve(A, B);
}

/** Solve A·z = b for a square matrix via Gaussian elimination with pivoting. */
function gaussianSolve(A: number[][], b: number[]): number[] {
  const n = b.length;
  // Augmented matrix.
  const m = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    // Partial pivot.
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(m[r][col]) > Math.abs(m[pivot][col])) pivot = r;
    }
    if (pivot !== col) {
      const tmp = m[pivot];
      m[pivot] = m[col];
      m[col] = tmp;
    }
    const diag = m[col][col] || 1e-12;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const factor = m[r][col] / diag;
      if (factor === 0) continue;
      for (let c = col; c <= n; c++) m[r][c] -= factor * m[col][c];
    }
  }
  const z = new Array(n).fill(0);
  for (let i = 0; i < n; i++) z[i] = m[i][n] / (m[i][i] || 1e-12);
  return z;
}

/** Rotate a point around a centre by `deg` degrees (screen-space, +ve = CW). */
function rotatePoint(p: Pt, cx: number, cy: number, deg: number): Pt {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - cx;
  const dy = p.y - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/**
 * Build the source quad (the region of the input that is stretched to fill the
 * output rectangle). Corners ordered TL, TR, BR, BL.
 */
function buildSourceQuad(w: number, h: number, o: VerticalsOptions): Pt[] {
  const v = clamp(o.vertical, -1, 1);
  const hk = clamp(o.horizontal, -1, 1);

  const topInset = Math.max(0, v) * MAX_INSET * w; // widen top
  const botInset = Math.max(0, -v) * MAX_INSET * w; // widen bottom
  const leftInset = Math.max(0, hk) * MAX_INSET * h; // widen left
  const rightInset = Math.max(0, -hk) * MAX_INSET * h; // widen right

  let corners: Pt[] = [
    { x: topInset, y: leftInset }, // TL
    { x: w - topInset, y: rightInset }, // TR
    { x: w - botInset, y: h - rightInset }, // BR
    { x: botInset, y: h - leftInset }, // BL
  ];

  if (Math.abs(o.rotate) > 1e-3) {
    corners = corners.map((p) => rotatePoint(p, w / 2, h / 2, o.rotate));
  }

  if (o.autoFit) corners = fitQuadToBounds(corners, w, h);
  return corners;
}

/**
 * Scale the quad about its centroid by the largest factor ≤ 1 that keeps every
 * corner inside [0,w] x [0,h]. This guarantees the warped output is fully
 * covered (no transparent borders) by zooming in slightly when needed.
 */
function fitQuadToBounds(corners: Pt[], w: number, h: number): Pt[] {
  const cx = (corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4;
  const cy = (corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4;

  let s = 1;
  for (const p of corners) {
    const dx = p.x - cx;
    const dy = p.y - cy;
    if (dx < 0) s = Math.min(s, cx / -dx); // p'.x >= 0
    else if (dx > 0) s = Math.min(s, (w - cx) / dx); // p'.x <= w
    if (dy < 0) s = Math.min(s, cy / -dy); // p'.y >= 0
    else if (dy > 0) s = Math.min(s, (h - cy) / dy); // p'.y <= h
  }
  s = clamp(s, 0.01, 1);
  if (s >= 0.9999) return corners;
  return corners.map((p) => ({
    x: cx + (p.x - cx) * s,
    y: cy + (p.y - cy) * s,
  }));
}

/**
 * Warp `src` using the given options and return a new same-size ImageData.
 * Output dimensions match the input exactly (no padding), matching the rest of
 * the studio editors and the worker's geometry snap-back.
 */
export function warpVerticals(
  src: ImageData,
  o: VerticalsOptions
): ImageData {
  const w = src.width;
  const h = src.height;
  const out = new ImageData(w, h);

  if (isIdentityVerticals(o)) {
    out.data.set(src.data);
    return out;
  }

  const dstCorners: Pt[] = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  const srcCorners = buildSourceQuad(w, h, o);
  const H = solveHomography(dstCorners, srcCorners);
  const [a, b, c, d, e, f, g, hh] = H;

  const sd = src.data;
  const od = out.data;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const denom = g * x + hh * y + 1;
      const u = (a * x + b * y + c) / denom;
      const vv = (d * x + e * y + f) / denom;
      const oi = (y * w + x) * 4;

      // Bilinear sample of the source at (u, vv); transparent outside bounds.
      if (u < 0 || vv < 0 || u > w - 1 || vv > h - 1) {
        od[oi] = 0;
        od[oi + 1] = 0;
        od[oi + 2] = 0;
        od[oi + 3] = 0;
        continue;
      }
      const x0 = u | 0;
      const y0 = vv | 0;
      const x1 = x0 + 1 < w ? x0 + 1 : x0;
      const y1 = y0 + 1 < h ? y0 + 1 : y0;
      const fx = u - x0;
      const fy = vv - y0;
      const w00 = (1 - fx) * (1 - fy);
      const w10 = fx * (1 - fy);
      const w01 = (1 - fx) * fy;
      const w11 = fx * fy;

      const i00 = (y0 * w + x0) * 4;
      const i10 = (y0 * w + x1) * 4;
      const i01 = (y1 * w + x0) * 4;
      const i11 = (y1 * w + x1) * 4;

      od[oi] =
        sd[i00] * w00 + sd[i10] * w10 + sd[i01] * w01 + sd[i11] * w11;
      od[oi + 1] =
        sd[i00 + 1] * w00 +
        sd[i10 + 1] * w10 +
        sd[i01 + 1] * w01 +
        sd[i11 + 1] * w11;
      od[oi + 2] =
        sd[i00 + 2] * w00 +
        sd[i10 + 2] * w10 +
        sd[i01 + 2] * w01 +
        sd[i11 + 2] * w11;
      od[oi + 3] =
        sd[i00 + 3] * w00 +
        sd[i10 + 3] * w10 +
        sd[i01 + 3] * w01 +
        sd[i11 + 3] * w11;
    }
  }
  return out;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

/**
 * Apply vertical/lens correction to a full-resolution image URL and return a
 * JPEG/PNG Blob. Preserves the source dimensions exactly.
 */
export async function applyVerticalsToImageUrlAsBlob(
  url: string,
  options: VerticalsOptions,
  mimeType: 'image/jpeg' | 'image/png' = 'image/jpeg',
  quality = 0.95
): Promise<Blob> {
  const img = await loadImage(url);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas context failed');
  ctx.drawImage(img, 0, 0);
  if (!isIdentityVerticals(options)) {
    const base = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const warped = warpVerticals(base, options);
    // JPEG has no alpha; paint a white backdrop so any transparent corners
    // (autoFit off) export cleanly rather than as black.
    if (mimeType === 'image/jpeg') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const tmp = document.createElement('canvas');
      tmp.width = canvas.width;
      tmp.height = canvas.height;
      tmp.getContext('2d')?.putImageData(warped, 0, 0);
      ctx.drawImage(tmp, 0, 0);
    } else {
      ctx.putImageData(warped, 0, 0);
    }
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      mimeType,
      quality
    );
  });
}
